import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Full-viewport loading overlay for Aurrica.
 *
 * Light translucent wash (no blur) so the UI stays visible. Centered spinner,
 * thin “Aurrica” wordmark sweep, and “Loading…” label.
 */
@Component({
  selector: 'lib-ui-loading-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (loading()) {
      <div
        class="li-overlay"
        role="status"
        aria-live="polite"
        [attr.aria-label]="message() || 'Loading'"
      >
        <div class="li-content">
          <div class="li-brand-row" aria-hidden="true">
            <svg
              class="li-spinner"
              width="26"
              height="26"
              viewBox="0 0 26 26"
              aria-hidden="true"
            >
              <circle class="li-spinner-track" cx="13" cy="13" r="10" />
              <circle class="li-spinner-arc" cx="13" cy="13" r="10" />
            </svg>

            <svg
              class="li-word-svg"
              viewBox="0 0 220 52"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <text
                x="110"
                y="36"
                text-anchor="middle"
                class="li-text-ghost"
                font-weight="100"
              >
                Aurrica
              </text>
              <text
                x="110"
                y="36"
                text-anchor="middle"
                class="li-text-shine"
                pathLength="340"
                font-weight="100"
              >
                Aurrica
              </text>
            </svg>
          </div>

          <p class="li-label" aria-hidden="true">
            {{ message() || 'Loading' }}<span class="li-dot">.</span
            ><span class="li-dot">.</span><span class="li-dot">.</span>
          </p>
        </div>
      </div>
    }
  `,
  styles: [
    `
      :host {
        /* Soft wash — same layout as before, much lighter than the old 0.92 center */
        --li-vignette-center: rgba(248, 250, 252, 0.52);
        --li-vignette-mid: rgba(241, 245, 249, 0.28);
        --li-vignette-edge: rgba(248, 250, 252, 0);

        --li-text-ghost: #64748b;
        --li-text-shine: #1d4ed8;

        --li-spinner-track: #94a3b8;
        --li-spinner-arc: #2563eb;

        --li-label: #475569;
      }

      :host-context(.dark) {
        --li-vignette-center: rgba(2, 6, 23, 0.55);
        --li-vignette-mid: rgba(2, 6, 23, 0.28);
        --li-vignette-edge: rgba(2, 6, 23, 0);

        --li-text-ghost: #94a3b8;
        --li-text-shine: #93c5fd;

        --li-spinner-track: #334155;
        --li-spinner-arc: #60a5fa;

        --li-label: #cbd5e1;
      }

      .li-overlay {
        position: fixed;
        inset: 0;
        z-index: 100000;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        background: radial-gradient(
          ellipse min(540px, 92vw) min(400px, 72vh) at 50% 44%,
          var(--li-vignette-center) 0%,
          var(--li-vignette-mid) 52%,
          var(--li-vignette-edge) 100%
        );
      }

      .li-content {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.75rem;
        filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.85));
      }

      :host-context(.dark) .li-content {
        filter: drop-shadow(0 0 16px rgba(2, 6, 23, 0.9));
      }

      .li-brand-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1.125rem;
      }

      /* ── Spinner ─────────────────────────────────────────────────────── */

      .li-spinner {
        flex-shrink: 0;
        overflow: visible;
      }

      .li-spinner-track {
        fill: none;
        stroke: var(--li-spinner-track);
        stroke-width: 1.25;
      }

      .li-spinner-arc {
        fill: none;
        stroke: var(--li-spinner-arc);
        stroke-width: 1.25;
        stroke-linecap: round;
        stroke-dasharray: 16 47;
        transform-origin: 13px 13px;
        animation: li-spinner-rotate 0.9s linear infinite;
      }

      @keyframes li-spinner-rotate {
        to {
          transform: rotate(360deg);
        }
      }

      /* ── Wordmark ────────────────────────────────────────────────────── */

      .li-word-svg {
        width: 220px;
        height: 52px;
        overflow: visible;
      }

      .li-text-ghost {
        fill: none;
        stroke: var(--li-text-ghost);
        stroke-width: 0.75;
        font-family:
          Inter,
          system-ui,
          -apple-system,
          'Segoe UI',
          Roboto,
          Helvetica,
          Arial,
          sans-serif;
        font-size: 34px;
        font-weight: 100;
        letter-spacing: 0.1em;
      }

      .li-text-shine {
        fill: none;
        stroke: var(--li-text-shine);
        stroke-width: 0.75;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-dasharray: 44 296;
        stroke-dashoffset: 0;
        font-family:
          Inter,
          system-ui,
          -apple-system,
          'Segoe UI',
          Roboto,
          Helvetica,
          Arial,
          sans-serif;
        font-size: 34px;
        font-weight: 100;
        letter-spacing: 0.1em;
        animation: li-text-sweep 2.1s linear infinite;
      }

      :host-context(.dark) .li-text-shine {
        stroke-width: 0.85;
      }

      @keyframes li-text-sweep {
        to {
          stroke-dashoffset: -340;
        }
      }

      .li-label {
        margin: 0;
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--li-label);
        user-select: none;
      }

      .li-dot {
        display: inline-block;
        opacity: 0;
        animation: li-dot-fade 1.4s ease-in-out infinite;
      }

      .li-dot:nth-child(2) {
        animation-delay: 0.2s;
      }
      .li-dot:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes li-dot-fade {
        0%,
        100% {
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
          animation-duration: 0.001ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.001ms !important;
        }
      }
    `,
  ],
})
export class LoadingIndicator {
  loading = input.required<boolean>();
  message = input<string>();
}
